import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import { DateFormat } from '../../../_metronic/helpers/components/DateFormat'
import Loader from '../../../_metronic/layout/components/loader/Loader'
import { getOfferListApi } from '../../redux/slices/DashboardSlice'
import { getDashboardList } from '../../services/DashboardService'
import { AppDispatch } from '../../redux/store'
import { FC, useEffect, useState } from 'react'
import Table from '../../modules/crm/components/common/Table'
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

type Props = {
    className: string
}

const TransactionListTable: React.FC<Props> = ({ className }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [dashboardData, setDashboardData] = useState<any[]>([])

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setIsLoading(true)
                const response = await getDashboardList()

                // Adjust based on response structure
                const mainData = response?.data?.data?.last_transactions ?? []
                setDashboardData(mainData)
            } catch (error) {
                console.error('Error fetching dashboard data:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchDashboardData()
    }, [])

    const columnData = [
        {
            name: 'Client Details',
            cell: (row: any) => (
                <div className='d-flex flex-column py-2'>
                    <div className='mb-1'>
                        <i className='bi bi-person-fill text-primary me-2' />
                        <span className='text-dark fw-semibold fs-7'>{row?.client_detail?.name || '-'}</span>
                    </div>
                    <div>
                        <i className='bi bi-telephone-fill text-muted me-2' />
                        <span className='text-muted fw-semibold fs-7'>{row?.client_detail?.phone || '-'}</span>
                    </div>
                </div>
            ),
            minWidth: '200px',
            sortable: true,
        },
        {
            name: 'Transaction Details',
            cell: (row: any) => (
                <div className='d-flex flex-column py-2'>
                    <div className='mb-1'>
                        <i className='bi bi-gift-fill text-primary me-2' />
                        <span className='text-dark fw-semibold fs-7'>{row?.gift_gallery_detail?.name || '-'}</span>
                    </div>
                    <div className='mb-1'>
                        <i className='bi bi-ticket-perforated text-muted me-2' />
                        <span className='text-muted fw-semibold fs-7'>{row?.coupon_detail?.name || '-'}</span>
                    </div>
                    <div>
                        <i className='bi bi-upc text-muted me-2' />
                        <span className='text-muted fw-semibold fs-7'>{row?.coupon_code || '-'}</span>
                    </div>
                </div>
            ),
            minWidth: '220px',
            sortable: true,
        },
        {
            name: 'Amount Details',
            cell: (row: any) => (
                <div className='d-flex flex-column py-2'>
                    <div className='mb-1'>
                        <i className='bi bi-wallet2 text-primary me-2' />
                        <span className='text-dark fw-semibold fs-7'>Points: {row?.points || 0}</span>
                    </div>
                    <div className='mb-1'>
                        <i className='bi bi-currency-rupee text-muted me-2' />
                        <span className='text-muted fw-semibold fs-7'>
                            Money: ₹{row?.money ? Number(row.money).toFixed(2) : '0.00'}
                        </span>
                    </div>
                    <div>
                        <span
                            className={`badge ${row?.action_id == 1 ? 'badge-success' : 'badge-danger'} fw-bold`}
                            style={{
                                padding: '4px 12px',
                                fontSize: '0.7rem',
                                letterSpacing: '0.3px',
                            }}
                        >
                            {row?.action_id == 1 ? 'CREDIT' : 'DEBIT'}
                        </span>
                    </div>
                </div>
            ),
            minWidth: '180px',
            sortable: true,
        },
        {
            name: 'Date & Time',
            cell: (row: any) => {
              const location = row?.scanned_location_at || '-'
              const formattedDate = row?.created_at
                ? new Date(row.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '-'
              const formattedTime = row?.created_at
                ? new Date(row.created_at).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                  })
                : '-'
          
              return (
                <div className='d-flex flex-column py-2'>
                  <div className='mb-1 d-flex align-items-center'>
                    <i className='bi bi-geo-alt-fill text-info me-2' />
                    <OverlayTrigger
                      placement='top'
                      overlay={<Tooltip id={`tooltip-location-${location}`}>{location}</Tooltip>}
                    >
                      <span className='text-dark fw-semibold fs-7 text-truncate' style={{maxWidth: 180}}>
                        {location}
                      </span>
                    </OverlayTrigger>
                  </div>
                  <div className='mb-1 d-flex align-items-center'>
                    <i className='bi bi-calendar-check-fill text-primary me-2' />
                    <span className='text-dark fw-semibold fs-7'>{formattedDate}</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <i className='bi bi-clock-fill text-muted me-2' />
                    <span className='text-muted fw-semibold fs-7'>{formattedTime}</span>
                  </div>
                </div>
              )
            },
            minWidth: '180px',
            sortable: true,
          },
        {
            name: 'Status',
            cell: (row: any) => {
                const status = row?.status?.toLowerCase() || 'pending'
                return (
                    <div className='d-flex flex-column align-items-start py-2'>
                        <span
                            className={`badge ${status === 'approved'
                                    ? 'badge-success'
                                    : status === 'rejected'
                                        ? 'badge-danger'
                                        : status === 'pending'
                                            ? 'badge-warning'
                                            : 'badge-secondary'
                                } fw-bold`}
                            style={{
                                padding: '8px 16px',
                                fontSize: '0.75rem',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                minWidth: '100px',
                                textAlign: 'center',
                            }}
                        >
                            {row?.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Pending'}
                        </span>
                    </div>
                )
            },
            minWidth: '130px',
            sortable: true,
        },
    ]

    // No Data Found Component
    const NoDataFound = () => (
        <div
            className='d-flex flex-column align-items-center justify-content-center py-10'
            style={{ minHeight: '400px' }}
        >
            <div
                className='d-flex align-items-center justify-content-center mb-4'
                style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    backgroundColor: '#f1f3f5',
                }}
            >
                <i className='bi bi-inbox fs-1 text-muted' style={{ fontSize: '4rem' }}></i>
            </div>
            <h3 className='text-gray-700 fw-bold mb-2'>No Transactions Found</h3>
            <p className='text-muted fs-6 text-center mb-4' style={{ maxWidth: '400px' }}>
                There are no recent transactions to display at the moment.
            </p>
            <div className='d-flex gap-2 align-items-center text-muted fs-7'>
                <i className='bi bi-info-circle'></i>
                <span>New transactions will appear here automatically</span>
            </div>
        </div>
    )

    return (
        <div className={`card ${className}`}>
            {/* begin::Header */}
            <div className='card-header border-0 py-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-3 mb-1'>Last 20 Transactions</span>
                    <span className='text-muted fw-semibold fs-7'>Recent transaction entries</span>
                </h3>
            </div>

            {/* begin::Body */}
            <div className='card-body p-0 shadow-sm'>
                {isLoading ? (
                    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
                        <Loader />
                    </div>
                ) : dashboardData?.length > 0 ? (
                    <Table
                        data={dashboardData.slice(0, 20)}
                        columns={columnData}
                        isLoading={isLoading}
                        showPagination={false}
                        onRowClicked={() => { }}
                    />
                ) : (
                    <NoDataFound />
                )}
            </div>
        </div>
    )
}

export { TransactionListTable }
